import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ClipboardList } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function StockTakes() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [takes, setTakes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/takes`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load stock takes');
      setTakes(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!isStaff) { navigate('/'); return; } load(); }, [isStaff, navigate, load]);

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff/stock')} data-testid="takes-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Stock">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Stock Takes</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Daily snapshots — retained for 14 days, then auto-purged</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5">
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : takes.length === 0 ? (
          <div className="text-center py-16 text-gray-500 italic">— no stock takes recorded yet —</div>
        ) : (
          <Card className="divide-y divide-gray-100">
            {takes.map(t => (
              <div key={t.id} data-testid={`take-row-${t.id}`} className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900">{fmt(t.created_at)}</div>
                  <div className="text-xs text-gray-500 mt-0.5">by {t.performed_by_name || '—'}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase text-gray-500">SKUs / Units</div>
                  <div className="text-lg font-bold">{t.product_count} · {t.total_units}</div>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
